// Ticket 25: Gift Animation Component
import React, { useEffect } from "react";

const ANIMATION_DURATION = 2600; // ms

export function GiftAnimation({ animation, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, ANIMATION_DURATION);
    return () => clearTimeout(t);
  }, [onDone]);

  const { type, giftName, giftIcon, fromUserName, toUserName } = animation;

  const label = buildLabel({ giftName, fromUserName, toUserName });

  switch (type) {
    case "rose":
    case "rose-bloom":
      return <RoseAnimation giftIcon={giftIcon} label={label} />;

    case "champagne":
    case "champagne-pop":
      return <ChampagneAnimation giftIcon={giftIcon} label={label} />;

    case "diamond":
    case "diamond-burst":
      return <DiamondAnimation giftIcon={giftIcon} label={label} />;

    case "fireworks":
    case "fireworks-burst":
      return <FireworksAnimation giftIcon={giftIcon} label={label} />;

    default:
      return <GenericGiftAnimation giftIcon={giftIcon} label={label} />;
  }
}

function buildLabel({ giftName, fromUserName, toUserName }) {
  if (fromUserName && toUserName) {
    return `${fromUserName} sent ${giftName || "a gift"} to ${toUserName}`;
  }
  if (fromUserName) {
    return `${fromUserName} sent you ${giftName || "a gift"}`;
  }
  return giftName || "New gift!";
}

function BaseCard({ children }) {
  return (
    <div className="relative flex flex-col items-center justify-center rounded-3xl bg-slate-900/95 px-8 py-6 shadow-2xl border border-slate-700/50">
      {children}
    </div>
  );
}

function GiftIcon({ giftIcon, fallbackEmoji, className = "" }) {
  if (giftIcon && giftIcon.startsWith('http')) {
    return (
      <img
        src={giftIcon}
        alt=""
        className={`h-20 w-20 rounded-full object-contain shadow-xl ${className}`}
      />
    );
  }
  return (
    <div className={`flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-slate-800 text-5xl shadow-lg ${className}`}>
      {giftIcon || fallbackEmoji}
    </div>
  );
}

function Label({ children }) {
  return (
    <p className="mt-3 text-center text-sm font-semibold text-slate-50 drop-shadow-lg max-w-xs">
      {children}
    </p>
  );
}

function RoseAnimation({ giftIcon, label }) {
  return (
    <div className="relative flex animate-gift-pop items-center justify-center">
      <div className="absolute inset-0 animate-rose-petals overflow-hidden">
        {/* decorative petals */}
        <div className="rose-petal rose-petal-1" />
        <div className="rose-petal rose-petal-2" />
        <div className="rose-petal rose-petal-3" />
      </div>
      <BaseCard>
        <GiftIcon giftIcon={giftIcon} fallbackEmoji="🌹" />
        <Label>{label}</Label>
      </BaseCard>
    </div>
  );
}

function ChampagneAnimation({ giftIcon, label }) {
  return (
    <div className="relative flex animate-gift-pop items-center justify-center">
      <div className="absolute inset-0 animate-champagne-bubbles overflow-hidden">
        <div className="bubble bubble-1" />
        <div className="bubble bubble-2" />
        <div className="bubble bubble-3" />
      </div>
      <BaseCard>
        <GiftIcon giftIcon={giftIcon} fallbackEmoji="🍾" className="animate-champagne-pop" />
        <Label>{label}</Label>
      </BaseCard>
    </div>
  );
}

function DiamondAnimation({ giftIcon, label }) {
  return (
    <div className="relative flex animate-gift-pop items-center justify-center">
      <div className="absolute inset-0 animate-diamond-rays overflow-hidden">
        <div className="ray ray-1" />
        <div className="ray ray-2" />
        <div className="ray ray-3" />
      </div>
      <BaseCard>
        <GiftIcon giftIcon={giftIcon} fallbackEmoji="💎" className="animate-diamond-spin" />
        <Label>{label}</Label>
      </BaseCard>
    </div>
  );
}

function FireworksAnimation({ giftIcon, label }) {
  return (
    <div className="relative flex animate-gift-pop items-center justify-center">
      <div className="absolute inset-0 animate-fireworks overflow-hidden">
        <div className="firework firework-1" />
        <div className="firework firework-2" />
        <div className="firework firework-3" />
      </div>
      <BaseCard>
        <GiftIcon giftIcon={giftIcon} fallbackEmoji="🎆" />
        <Label>{label}</Label>
      </BaseCard>
    </div>
  );
}

function GenericGiftAnimation({ giftIcon, label }) {
  return (
    <div className="flex animate-gift-pop items-center justify-center">
      <BaseCard>
        <GiftIcon giftIcon={giftIcon} fallbackEmoji="🎁" />
        <Label>{label}</Label>
      </BaseCard>
    </div>
  );
}
